/**
 * UNTRUSTED: bridge replies are model input, not facts — kernel output can run script in the lab window and post as the extension.
 *
 * The console half of the JupyterLab bridge (`script-runtime/labextension/src/index.ts` is the other half).
 *
 * HANDSHAKE: on every iframe load the console posts `{type:'gisify-bridge-hello'}` to the frame and repeats it every
 * 500 ms until it sees `{type:'gisify-bridge-ready', version: 1}` (at most `MAX_HELLOS` times; once one hello lands
 * the extension remembers this origin and sends `ready` by itself whenever a notebook becomes ready). `state` is
 * `connecting` while it says hello, `ready` once answered, and `unavailable` when the hellos ran out unanswered
 * (an image without the extension, or a broken frame); a reload starts `connecting` again.
 *
 * REQUESTS `{type:'gisify-bridge', id, op, args}`, one reply each `{type:'gisify-bridge-reply', id, ok, result?, error?}`.
 *
 * Messages go ONLY to the frame's `contentWindow`, with `targetOrigin` = the origin parsed from the embed URL (never
 * `'*'`), and a message is read only when BOTH its origin is that origin AND its source is that window.
 */

export type BridgeOp = 'snapshot' | 'apply_ops' | 'run' | 'interrupt'

/** What the page context says about the bridge (`scripts.notebook.bridge`). */
export type BridgeState = 'connecting' | 'ready' | 'unavailable'

/** What a bridge request rejects with: the extension's `{code, message}` or one of the console's own codes. */
export interface BridgeError {
  code: string
  message: string
  op_index?: number
}

/** The handle the assistant's executors use (exposed on the scripts surface as `bridge`). */
export interface NotebookBridgeHandle {
  readonly ready: boolean
  request<T>(op: BridgeOp, args?: unknown, timeoutMs?: number): Promise<T>
}

/** From the last `snapshot`: what the page context tells the model about the live notebook. */
export interface BridgeSummary {
  cellCount: number
  activeCell: number
  dirty: boolean
}

export interface NotebookBridgeClient extends NotebookBridgeHandle {
  readonly summary: BridgeSummary | null
  readonly state: BridgeState
  /** The iframe (re)loaded: not ready until it says so again; start saying hello. */
  frameLoaded(): void
  /** The iframe is going away (remount, url change, error overlay): not ready now; pending requests reject `bridge_closed`. */
  frameUnloading(): void
  start(): void
  /** Stop listening and saying hello; pending requests reject `bridge_closed`. */
  dispose(): void
}

export const BRIDGE_VERSION = 1
export const BRIDGE_TIMEOUT_MS = 10_000
/** The extension waits up to 120 s for a run; this leaves it room to answer. */
export const RUN_TIMEOUT_MS = 130_000
export const HELLO_INTERVAL_MS = 500
/** Two minutes of hellos; the extension needs only one to land. */
export const MAX_HELLOS = 240

interface BridgeTarget {
  postMessage(message: unknown, targetOrigin: string): void
}

interface MessageHost {
  addEventListener(type: 'message', listener: (e: MessageEvent) => void): void
  removeEventListener(type: 'message', listener: (e: MessageEvent) => void): void
}

export interface NotebookBridgeDeps {
  /** Where replies arrive (the console's `window`). */
  host: MessageHost
  /** The frame's `contentWindow`, read on every send and every message (a reload is a new window). */
  target(): BridgeTarget | null | undefined
  /** The live origin from the embed URL, or null when it cannot be parsed. */
  origin(): string | null
  newId?: () => string
}

/** `https://live.<base>` from the embed URL; null for anything that is not an absolute https URL. */
export function bridgeOrigin(embedUrl: string): string | null {
  try {
    const url = new URL(embedUrl)

    return url.protocol === 'https:' ? url.origin : null
  } catch {
    return null
  }
}

const fail = (code: string, message: string): BridgeError => ({ code, message })

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null

function toBridgeError(raw: unknown): BridgeError {
  if (!isRecord(raw)) return fail('bridge_error', 'The notebook refused the request.')

  const out: BridgeError = {
    code: typeof raw.code === 'string' ? raw.code : 'bridge_error',
    message: typeof raw.message === 'string' ? raw.message : 'The notebook refused the request.',
  }
  if (typeof raw.op_index === 'number') out.op_index = raw.op_index

  return out
}

function toSummary(result: unknown): BridgeSummary | null {
  if (!isRecord(result)) return null
  const { cell_count: cellCount, active_index: activeCell, dirty } = result
  if (typeof cellCount !== 'number' || typeof activeCell !== 'number' || typeof dirty !== 'boolean') return null

  return { cellCount, activeCell, dirty }
}

let counter = 0
const defaultId = () =>
  `b${Date.now().toString(36)}-${(counter++).toString(36)}-${Math.random().toString(36).slice(2, 8)}`

interface Pending {
  resolve(value: unknown): void
  reject(error: BridgeError): void
  timer: ReturnType<typeof setTimeout>
}

export function createNotebookBridgeClient(deps: NotebookBridgeDeps): NotebookBridgeClient {
  const newId = deps.newId ?? defaultId
  const pending = new Map<string, Pending>()
  let ready = false
  let unavailable = false
  let summary: BridgeSummary | null = null
  let hello: ReturnType<typeof setInterval> | null = null
  let listening = false

  const stopHello = () => {
    if (hello) clearInterval(hello)
    hello = null
  }

  const post = (message: unknown): boolean => {
    const target = deps.target()
    const origin = deps.origin()
    if (!target || !origin) return false
    try {
      target.postMessage(message, origin)

      return true
    } catch {
      return false
    }
  }

  const rejectAll = (error: BridgeError) => {
    for (const [id, p] of pending) {
      clearTimeout(p.timer)
      pending.delete(id)
      p.reject(error)
    }
  }

  const refresh = () => void client.request('snapshot').catch(() => undefined)

  const onMessage = (e: MessageEvent) => {
    const target = deps.target()
    if (!target || e.source !== target || e.origin !== deps.origin()) return
    const data: unknown = e.data
    if (!isRecord(data)) return

    if (data.type === 'gisify-bridge-ready' && data.version === BRIDGE_VERSION) {
      ready = true
      unavailable = false
      stopHello()
      refresh()

      return
    }

    if (data.type !== 'gisify-bridge-reply' || typeof data.id !== 'string') return
    const p = pending.get(data.id)
    if (!p) return
    clearTimeout(p.timer)
    pending.delete(data.id)
    if (data.ok === true) p.resolve(data.result)
    else p.reject(toBridgeError(data.error))
  }

  const client: NotebookBridgeClient = {
    get ready() {
      return ready
    },
    get summary() {
      return summary
    },
    get state(): BridgeState {
      if (ready) return 'ready'

      return unavailable ? 'unavailable' : 'connecting'
    },
    start() {
      if (listening) return
      deps.host.addEventListener('message', onMessage)
      listening = true
    },
    dispose() {
      stopHello()
      if (listening) deps.host.removeEventListener('message', onMessage)
      listening = false
      ready = false
      summary = null
      rejectAll(fail('bridge_closed', 'The notebook tab closed.'))
    },
    frameUnloading() {
      stopHello()
      ready = false
      unavailable = false
      summary = null
      rejectAll(fail('bridge_closed', 'The notebook tab reloaded.'))
    },
    frameLoaded() {
      client.frameUnloading()
      let sent = 0

      const sayHello = () => {
        post({ type: 'gisify-bridge-hello' })
        if (++sent < MAX_HELLOS) return
        stopHello()
        // A ready already received would have stopped the hellos; this many unanswered means no bridge.
        unavailable = !ready
      }

      sayHello()
      hello = setInterval(sayHello, HELLO_INTERVAL_MS)
    },
    request<T>(op: BridgeOp, args?: unknown, timeoutMs?: number): Promise<T> {
      if (!ready) return Promise.reject(fail('bridge_not_ready', 'The notebook tab is not connected yet.'))

      const id = newId()
      const wait = timeoutMs ?? (op === 'run' ? RUN_TIMEOUT_MS : BRIDGE_TIMEOUT_MS)
      const answer = new Promise<unknown>((resolve, reject) => {
        const timer = setTimeout(() => {
          pending.delete(id)
          reject(fail('bridge_timeout', 'The notebook tab did not answer.'))
        }, wait)
        pending.set(id, { resolve, reject, timer })
        if (!post({ type: 'gisify-bridge', id, op, args })) {
          clearTimeout(timer)
          pending.delete(id)
          reject(fail('bridge_not_ready', 'The notebook tab is not connected yet.'))
        }
      })

      // The context's cell count, active cell and dirty flag come from the LAST snapshot: record one, and take a
      // fresh one after any other op, whatever its outcome.
      if (op === 'snapshot') {
        return answer.then(result => {
          summary = toSummary(result) ?? summary

          return result as T
        })
      }

      return answer.then(
        result => {
          refresh()

          return result as T
        },
        (err: unknown) => {
          refresh()
          throw err
        },
      )
    },
  }

  return client
}
